/**
 * Path data for the Zuul logo, lifted verbatim from the source SVGs in `src/assets/images`
 * (`zuulnotext`, `zuulwithtextside`, `zuulwithtext`) so the art can be inlined and themed.
 *
 * The source files paint the figures and wordmark white with two red accent strokes, which is why
 * they used to be rendered as <img> under an `invert(1)` filter in light mode. Inlined, the white
 * shapes become `currentColor` and the red ones the accent, so the logo takes the active theme
 * preset's colors instead. Paths flagged `accent` are the two red strokes.
 */

export interface LogoPath {
  d: string;
  /** Painted with the accent color rather than `currentColor`. */
  accent?: boolean;
}

export interface LogoGroup {
  /** Transform the source art applied to this group; omitted where it had none. */
  transform?: string;
  paths: LogoPath[];
}

export interface LogoArt {
  viewBox: string;
  groups: LogoGroup[];
}

export type ZuulLogoVariant = 'mark' | 'horizontal' | 'stacked';

export const ZUUL_LOGO_ART: Record<ZuulLogoVariant, LogoArt> = {
  // Mark only — the two gatekeeper figures, no wordmark. Used in the collapsed sidebar.
  mark: {
    viewBox: '0 0 825 380',
    groups: [
      {
        paths: [
          {
            d: 'm 304.49049,132.31878 -19.82853,66.92167 -13.82675,46.67823 -6.23864,21.05832 c -66.81861,-23.69857 -137.33633,0.67783 -180.294572,55.65801 -1.730374,-23.52539 0.658823,-47.81579 7.723332,-71.67134 5.611805,-18.95762 13.77168,-36.23941 23.92383,-51.5413 15.93868,-24.04619 36.80662,-43.19569 60.48445,-56.28223 7.7237,-4.26375 15.756,-7.89456 24.01151,-10.82136 32.66225,-11.62748 68.8663,-12.47853 104.04537,0 z',
          },
          {
            d: 'm 359.72132,352.29063 c -15.7321,10.99452 -35.65515,14.49451 -54.79352,7.70268 -32.79774,-11.62753 -51.28471,-49.21382 -42.64081,-85.23516 l 0.0394,-0.13956 2.223,-7.49455 0.0155,-0.0614 0.0319,-0.0863 c 42.06117,14.9194 75.08894,46.19974 95.12361,85.31362 z',
            accent: true,
          },
          {
            d: 'm 384.44574,132.31878 h -79.95525 c 0,-47.485985 -26.53521,-88.197816 -64.26188,-105.349252 12.31096,-5.601312 25.81313,-8.692964 39.98161,-8.692964 57.56317,0 104.23552,51.054845 104.23552,114.042216 z',
          },
          {
            d: 'm 515.65152,132.31878 19.82853,66.92167 13.82677,46.67823 6.23976,21.05832 c 66.81861,-23.69857 137.33623,0.67783 180.29366,55.65801 1.73108,-23.52539 -0.65827,-47.81579 -7.72253,-71.67134 -5.61229,-18.95762 -13.77215,-36.23941 -23.92384,-51.5413 -15.93936,-24.04619 -36.80762,-43.19569 -60.48544,-56.28223 -7.72259,-4.26375 -15.75604,-7.89456 -24.01042,-10.82136 -32.66335,-11.62748 -68.86624,-12.47853 -104.04649,0 z',
          },
          {
            d: 'm 460.41956,352.29063 c 15.73322,10.99452 35.65626,14.49451 54.79352,7.70268 32.79773,-11.62753 51.2847,-49.21382 42.64082,-85.23516 l -0.0394,-0.13956 -2.22189,-7.49455 -0.0158,-0.0614 -0.0319,-0.0863 c -42.06113,14.91947 -75.09008,46.19982 -95.12472,85.31369 z',
            accent: true,
          },
          {
            d: 'm 435.69399,132.31878 h 79.95526 c 0,-47.485985 26.53636,-88.197816 64.26305,-105.349252 -12.31097,-5.601312 -25.81321,-8.692964 -39.98163,-8.692964 -57.56318,0 -104.23668,51.054845 -104.23668,114.042216 z',
          },
        ],
      },
    ],
  },
  // Mark with the ZUUL wordmark beside it. Used in the expanded sidebar.
  horizontal: {
    viewBox: '0 0 2499 380',
    groups: [
      {
        transform: 'translate(-92.933895,15.122244) matrix(1.0722467,0,0,1.0722467,108.9006,-39.501527)',
        paths: [
          {
            d: 'm 320.27264,143.97899 -19.91843,63.95543 -13.88948,44.60925 -6.26691,20.12494 c -67.12162,-22.64815 -137.95911,0.64777 -181.11214,53.19101 -1.73822,-22.48264 0.66181,-45.6964 7.75835,-68.49459 5.63725,-18.11733 13.83413,-34.6331 24.03233,-49.25676 16.01094,-22.98037 36.97352,-41.28108 60.75872,-53.78758 7.75871,-4.07476 15.82744,-7.54462 24.1204,-10.3417 32.81034,-11.11212 69.17856,-11.92541 104.51716,0 z',
          },
          {
            d: 'm 375.75391,354.20077 c -15.80341,10.50722 -35.8168,13.85205 -55.04197,7.36125 -32.94647,-11.11212 -51.51725,-47.03243 -42.83418,-81.45715 l 0.04,-0.13336 2.23311,-7.16239 0.016,-0.0583 0.032,-0.0822 c 42.25189,14.2581 75.42945,44.15199 95.55495,81.53216 z',
            accent: true,
          },
          {
            d: 'm 400.59047,143.97899 h -80.31783 c 0,-45.381219 -26.65553,-84.288531 -64.5533,-100.679746 12.3668,-5.353038 25.93023,-8.307657 40.16292,-8.307657 57.82421,0 104.70821,48.791886 104.70821,108.987403 z',
          },
          {
            d: 'm 532.39123,143.97899 19.91843,63.95543 13.88948,44.60925 6.26806,20.12494 c 67.12161,-22.64815 137.95899,0.64777 181.11121,53.19101 1.73891,-22.48264 -0.66124,-45.6964 -7.75756,-68.49459 -5.6377,-18.11733 -13.83456,-34.6331 -24.03231,-49.25676 -16.01163,-22.98037 -36.97455,-41.28108 -60.75975,-53.78758 -7.75756,-4.07476 -15.82744,-7.54462 -24.11926,-10.3417 -32.81148,-11.11212 -69.17855,-11.92541 -104.5183,0 z',
          },
          {
            d: 'm 476.90881,354.20077 c 15.80456,10.50722 35.81795,13.85205 55.04197,7.36125 32.94647,-11.11212 51.51726,-47.03243 42.83419,-81.45715 l -0.04,-0.13336 -2.23197,-7.16239 -0.016,-0.0583 -0.032,-0.0822 c -42.25189,14.2581 -75.43059,44.15199 -95.5561,81.53216 z',
            accent: true,
          },
          {
            d: 'm 452.07112,143.97899 h 80.31782 c 0,-45.381219 26.65668,-84.288531 64.55445,-100.679746 -12.3668,-5.353038 -25.93023,-8.307657 -40.16292,-8.307657 -57.82421,0 -104.70935,48.791886 -104.70935,108.987403 z',
          },
        ],
      },
      {
        paths: [
          { d: 'M 2187.8524,338.27795 V 58.549833 h 66.3857 V 285.73445 h 145.8981 v 52.5435 z' },
          {
            d: 'm 2000.4422,341.86901 c -45.5073,0 -78.8252,-10.71025 -99.9538,-32.13086 -21.0032,-21.42061 -31.5048,-53.2366 -31.5048,-95.44776 V 58.549833 h 66.3855 V 219.96051 c 0,23.56278 5.3135,40.88823 15.9403,51.97653 10.7516,10.96235 27.1291,16.44354 49.1328,16.44354 22.0034,0 38.3184,-5.48119 48.9454,-16.44354 10.6266,-11.0883 15.9398,-28.41375 15.9398,-51.97653 V 58.549833 h 65.2606 V 214.29039 c 0,42.21116 -10.5019,74.02715 -31.5051,95.44776 -21.0034,21.42061 -53.8836,32.13086 -98.6407,32.13086 z',
          },
          {
            d: 'm 1682.887,341.86901 c -45.5072,0 -78.8251,-10.71025 -99.9536,-32.13086 -21.0033,-21.42061 -31.5048,-53.2366 -31.5048,-95.44776 V 58.549833 h 66.3856 V 219.96051 c 0,23.56278 5.3134,40.88823 15.9401,51.97653 10.7516,10.96235 27.1292,16.44354 49.1328,16.44354 22.0036,0 38.3185,-5.48119 48.9452,-16.44354 10.6266,-11.0883 15.9401,-28.41375 15.9401,-51.97653 V 58.549833 h 65.2596 V 214.29039 c 0,42.21116 -10.5016,74.02715 -31.5041,95.44776 -21.0035,21.42061 -53.8835,32.13086 -98.6409,32.13086 z',
          },
          {
            d: 'm 1359.3869,285.9234 h 149.2738 v 52.35455 h -235.35 V 302.36697 L 1420.3342,110.9044 H 1275.3734 V 58.549833 h 231.2245 v 35.910988 z',
          },
        ],
      },
    ],
  },
  // Mark with the ZUUL wordmark underneath. Used on the login card.
  stacked: {
    viewBox: '0 0 825 609',
    groups: [
      {
        transform: 'translate(-5.9495195,-34.991587)',
        paths: [
          {
            d: 'm 314.66338,143.97899 -19.91843,63.95543 -13.88948,44.60925 -6.26691,20.12494 c -67.12162,-22.64815 -137.95911,0.64777 -181.112141,53.19101 -1.73822,-22.48264 0.66181,-45.6964 7.758351,-68.49459 5.63725,-18.11733 13.83413,-34.6331 24.03233,-49.25676 16.01094,-22.98037 36.97352,-41.28108 60.75872,-53.78758 7.75871,-4.07476 15.82744,-7.54462 24.1204,-10.3417 32.81034,-11.11212 69.17856,-11.92541 104.51716,0 z',
          },
          {
            d: 'm 370.14465,354.20077 c -15.80341,10.50722 -35.8168,13.85205 -55.04197,7.36125 -32.94647,-11.11212 -51.51725,-47.03243 -42.83418,-81.45715 l 0.04,-0.13336 2.23311,-7.16239 0.016,-0.0583 0.032,-0.0822 c 42.25189,14.2581 75.42945,44.15199 95.55495,81.53216 z',
            accent: true,
          },
          {
            d: 'm 394.98121,143.97899 h -80.31783 c 0,-45.381219 -26.65553,-84.288531 -64.5533,-100.679746 12.3668,-5.353038 25.93023,-8.307657 40.16292,-8.307657 57.82421,0 104.70821,48.791886 104.70821,108.987403 z',
          },
          {
            d: 'm 526.78197,143.97899 19.91843,63.95543 13.88948,44.60925 6.26806,20.12494 c 67.12161,-22.64815 137.95899,0.64777 181.11121,53.19101 1.73891,-22.48264 -0.66124,-45.6964 -7.75756,-68.49459 -5.6377,-18.11733 -13.83456,-34.6331 -24.03231,-49.25676 -16.01163,-22.98037 -36.97455,-41.28108 -60.75975,-53.78758 -7.75756,-4.07476 -15.82744,-7.54462 -24.11926,-10.3417 -32.81148,-11.11212 -69.17855,-11.92541 -104.5183,0 z',
          },
          {
            d: 'm 471.29955,354.20077 c 15.80456,10.50722 35.81795,13.85205 55.04197,7.36125 32.94647,-11.11212 51.51726,-47.03243 42.83419,-81.45715 l -0.04,-0.13336 -2.23197,-7.16239 -0.016,-0.0583 -0.032,-0.0822 c -42.25189,14.2581 -75.43059,44.15199 -95.5561,81.53216 z',
            accent: true,
          },
          {
            d: 'm 446.46186,143.97899 h 80.31782 c 0,-45.381219 26.65668,-84.288531 64.55445,-100.679746 -12.3668,-5.353038 -25.93023,-8.307657 -40.16292,-8.307657 -57.82421,0 -104.70935,48.791886 -104.70935,108.987403 z',
          },
        ],
      },
      {
        paths: [
          { d: 'M 608.96296,602.60559 V 455.50597 h 35.17657 v 119.46876 h 77.30898 v 27.63086 z' },
          {
            d: 'm 509.65758,604.49403 c -24.11351,0 -41.76805,-5.63219 -52.96361,-16.89657 C 445.56466,576.33308 440,559.60217 440,537.40471 v -81.89874 h 35.17658 v 84.88048 c 0,12.39082 2.81545,21.50172 8.44635,27.33269 5.69715,5.76471 14.37537,8.64708 26.03465,8.64708 11.65928,0 20.30437,-2.88236 25.93527,-8.64708 5.6309,-5.83096 8.44635,-14.94187 8.44635,-27.33269 v -84.88048 h 34.58037 v 81.89874 c 0,22.19746 -5.56466,38.92837 -16.69397,50.19275 -11.12931,11.26438 -28.55199,16.89657 -52.26802,16.89657 z',
          },
          {
            d: 'm 341.39076,604.49403 c -24.11351,0 -41.76805,-5.63219 -52.96361,-16.89657 -11.12931,-11.26438 -16.69397,-27.99529 -16.69397,-50.19275 v -81.89874 h 35.17658 v 84.88048 c 0,12.39082 2.81545,21.50172 8.44636,27.33269 5.69714,5.76471 14.37536,8.64708 26.03464,8.64708 11.65928,0 20.30437,-2.88236 25.93527,-8.64708 5.6309,-5.83097 8.44635,-14.94187 8.44635,-27.33269 v -84.88048 h 34.57982 v 81.89874 c 0,22.19746 -5.56466,38.92837 -16.69342,50.19275 -11.12931,11.26438 -28.55199,16.89657 -52.26802,16.89657 z',
          },
          {
            d: 'm 169.97367,575.07412 h 79.09762 v 27.53147 H 124.36336 v -18.88441 l 77.90519,-100.68366 h -76.81213 v -27.53155 h 122.52181 v 18.8844 z',
          },
        ],
      },
    ],
  },
};
